import { watch } from "vue";
import { useRouter } from "vue-router";
import { boardChromeText } from "../i18n/practiceCopy";
import { useBoardContextStore } from "../store/pinia/boardContextStore";
import { usePracticeLocale } from "./usePracticeLocale";

const BOARD_ROUTE_NAMES = new Set([
  "lessons",
  "questions",
  "answers",
  "lesson",
  "question",
  "answer",
]);

const PRACTICE_TITLE_LEVELS = new Set(["practice", "practice-list"]);

function practiceDocumentTitle(
  chip: string,
  crumbs: readonly string[],
  hebrew: boolean,
): string {
  const chipText = boardChromeText(chip, hebrew);
  const trail = crumbs
    .map((text) => boardChromeText(text, hebrew))
    .filter(Boolean);
  const main = trail.length ? trail.join(" › ") : chipText;
  const app = hebrew ? "לוח מתמטיקה" : "Math Whiteboard";
  return `[${chipText}] ${main} | ${app}`;
}

/** Keeps browser tab title in sync with the active lesson / question / answer context. */
export function useBoardDocumentTitle() {
  const boardContext = useBoardContextStore();
  const router = useRouter();
  const { isHebrew } = usePracticeLocale();

  router.afterEach((to) => {
    if (!BOARD_ROUTE_NAMES.has(to.name as string)) {
      boardContext.clear();
    }
  });

  watch(
    () =>
      [
        boardContext.level,
        boardContext.chipLabel,
        boardContext.breadcrumbs.map((crumb) => crumb.text).join("\u0000"),
        boardContext.documentTitle,
        isHebrew.value,
      ] as const,
    ([level, chip, crumbKey, title, hebrew]) => {
      if (level === "none" || !title) return;
      if (PRACTICE_TITLE_LEVELS.has(level)) {
        document.title = practiceDocumentTitle(
          chip,
          crumbKey ? crumbKey.split("\u0000") : [],
          hebrew,
        );
        return;
      }
      document.title = title;
    },
    { immediate: true },
  );
}
